import { useState, type ReactNode } from "react";
import Lightbox from "../ui/Lightbox";
import Model3D from "../ui/Model3D";
import { Chevron, Cube } from "../ui/Icons";
import { track, trackOnce } from "../track";
import { toast } from "../ui/Toast";
import type { PreviewArtifact } from "../types";
import type { Words } from "./copy";
import type { ProofPair } from "./Proof";

/** The thing they came for, and the proof it is theirs.
 *
 *  Order of preference: a turntable video, which gives most of the feeling of
 *  the 3D at a tenth of the bytes and costs no interaction; then the drawing.
 *  The model itself is tens of megabytes and stays behind a deliberate tap.
 *
 *  One of these per piece. A request for three dogs draws three, each with its
 *  own photo underneath, its own 3D behind its own tap, and a heading that is
 *  only there to say which of three this is. */
export default function Hero({
  mockup,
  model,
  video,
  photoUrl,
  heading,
  feedback,
  look,
  base,
  stage,
  footer,
  id,
  className,
  prints,
  fixing,
  words: t,
}: {
  mockup?: PreviewArtifact;
  model?: PreviewArtifact;
  video?: PreviewArtifact;
  photoUrl: string | null;
  /** "Keychain · 2 of 3", or nothing at all when there is only one. */
  heading?: string | null;
  /** "Does it look right?" for this piece alone, on a page of several: under
   *  its own drawing and before its price, where the answer is about what they
   *  are looking at and not about whichever card they scrolled past last. */
  feedback?: ReactNode;
  /** The Christmas look for this piece (ChristmasLook.tsx): the hat and the
   *  sweater, put on or taken off with a tap. Straight under the drawing it
   *  changes, above their own photo, since what it answers to is the picture
   *  and not the price. It says nothing outside the season, or while a change
   *  of this piece is being drawn. */
  look?: ReactNode;
  /** Their figure on its base (BaseShowcase.tsx), straight under the drawing. */
  base?: ReactNode;
  /** Their figure standing on its base, shown in place of the drawing
   *  (BaseStage): a statue drawn on a transparent background is shown only on
   *  the base it is printed on. Told when it is drawn, and when their figure
   *  cannot be loaded, which puts the drawing back. */
  stage?: (events: { onReady: () => void; onFail: () => void }) => ReactNode;
  /** What this piece costs and how to order it, on a page of several. Inside
   *  this card rather than after it, so the price belongs to the drawing above
   *  it and not to whichever card comes next. */
  footer?: ReactNode;
  /** Where the rail of pieces scrolls to (Preview: `piece-…`). */
  id?: string;
  /** Extra classes on the card, such as the arrival animation. */
  className?: string;
  /** Someone else's preview beside the piece it became, of this product:
   *  one small pair under the drawing, so "this is how it prints" is said
   *  on the first screen rather than three cards down. The strip of them
   *  (Proof.tsx) still follows the price. Left out on a page of several
   *  pieces, where it would be said three times. */
  prints?: ProofPair | null;
  /** A change to this piece being fixed while they watch: shown where the
   *  drawing is, in place of it, until the new one is on the page. */
  fixing?: ReactNode;
  words: Words;
}) {
  const [show3d, setShow3d] = useState(false);
  const [loading3d, setLoading3d] = useState(false);
  const [loaded, setLoaded] = useState(false);
  /** Whether the turntable is actually moving. Until it is, the drawing under
   *  it is what they see. */
  const [playing, setPlaying] = useState(false);
  const [videoFailed, setVideoFailed] = useState(false);
  const [zoomed, setZoomed] = useState<string | null>(null);
  /** The pair opened full size: stepped through as preview, then printed. */
  const [zoomList, setZoomList] = useState<string[] | undefined>(undefined);
  const [stageFailed, setStageFailed] = useState(false);

  /* The 3D used to be a one-way door. The tap set show3d before the library had
     downloaded, the picture they came to see was replaced by an empty box, and
     if the import or the GLB failed there was no error, no retry and no way
     back — on a tens-of-megabyte model over 4G, which is not a rare path. The
     one thing that separates this from a JPEG in an email could leave them with
     nothing at all. So the library is fetched first, the picture stays until it
     is ready, and there is always a way back to it. */
  async function open3d() {
    if (!model) return;
    track("model.turned", { bytes: model.bytes });
    setLoading3d(true);
    try {
      await import("@google/model-viewer");
      setShow3d(true);
      // "Back to the picture" brings a new video element, which has to start
      // playing again before it covers the drawing, or it would cover it with
      // an empty box.
      setPlaying(false);
    } catch {
      toast(t.t("model3dFailed"), "bad", 5000);
    } finally {
      setLoading3d(false);
    }
  }

  const poster = mockup?.url;
  /* Nothing to wait for: a piece with no drawing and no turntable on the page
     (a studio look before every piece is drawn, or an artifact that did not
     match its piece). The frame used to wait for a load that could never
     come, and the skeleton shimmered for ever where the figurine should be. */
  const nothing = !mockup && (!video || videoFailed);

  /* The video used to be the only thing in the frame, shown once it had loaded
     data, with the drawing only as its poster. A video that never loads data
     (iOS in Low Power Mode, which refuses autoplay; a link that has expired; a
     dropped connection) left a pulsing grey box where their figurine should
     be, with the 3D button hidden behind it for good. So the drawing is always
     there underneath, and the video only fades in over it once it is playing:
     when it cannot play, they simply see the drawing. */
  const staged = !!stage && !!mockup && !stageFailed;
  const still = staged ? (
    <div className={`hero-stage-box${loaded ? " hero-in" : ""}`}>
      {stage({
        onReady: () => {
          setLoaded(true);
          trackOnce("hero.viewed");
        },
        onFail: () => setStageFailed(true),
      })}
    </div>
  ) : mockup && (
    <img
      className={`hero-img${loaded ? " hero-in" : ""}`}
      src={mockup.url}
      alt=""
      decoding="async"
      /* A picture already in the cache can finish before React has put its
         load listener on the element, and then onLoad never fires and the
         skeleton stays over a picture that is sitting right there. */
      ref={(node) => {
        if (node?.complete && !loaded) setLoaded(true);
      }}
      onClick={() => {
        setZoomed(mockup.url);
        trackOnce("photo.opened", { which: "mockup" });
      }}
      onLoad={() => {
        setLoaded(true);
        trackOnce("hero.viewed");
      }}
      onError={() => setLoaded(true)}
    />
  );

  return (
    <div id={id} className={className ? `card ${className}` : "card"}>
      {heading && (
        <p className="small" style={{ margin: "0 0 8px", fontWeight: 600 }}>
          {heading}
        </p>
      )}
      {fixing ? (
        fixing
      ) : show3d && model ? (
        <>
          <Model3D
            /* A redone model is a new download with its own stall timer and its
               own "ready", not an update to the one already on screen. */
            key={model.url}
            src={model.url}
            poster={poster}
            loadingLabel={t.t("loading3d")}
            /* Said once there is something to turn. Before it loads this would
               have been an instruction for a blank square. */
            hint={t.t("turn")}
            onReady={() => trackOnce("model.ready")}
            onFailed={() => {
              toast(t.t("model3dFailed"), "bad", 5000);
              setShow3d(false);
            }}
          />
          <button className="btn btn-quiet btn-sm btn-block" style={{ marginTop: 12 }} onClick={() => setShow3d(false)}>
            {t.t("backToPicture")}
          </button>
        </>
      ) : nothing ? (
        model && (
          <button
            className="btn btn-quiet btn-sm btn-block"
            disabled={loading3d}
            onClick={() => void open3d()}
          >
            <Cube size={15} />
            {t.t("see3d")}
          </button>
        )
      ) : (
        <div className={`hero-frame${loaded ? "" : " hero-waiting"}${staged ? " hero-staged" : ""}`}>
          {!loaded && <div className="skeleton hero-skeleton" />}

          {still}

          {video && !videoFailed && (
            <video
              className={`hero-img${(still ? playing : loaded) ? " hero-in" : ""}`}
              src={video.url}
              poster={still ? undefined : poster}
              autoPlay
              muted
              loop
              playsInline
              // All four together or iOS refuses to autoplay, and Low Power
              // Mode refuses regardless, which is what the drawing under it is
              // for. Laid exactly over the drawing, and left out of the way of
              // a tap on it until it is the thing on screen.
              style={
                still
                  ? {
                      position: "absolute",
                      inset: 0,
                      height: "100%",
                      pointerEvents: playing ? undefined : "none",
                    }
                  : undefined
              }
              onLoadedData={() => {
                if (still) return;
                setLoaded(true);
                trackOnce("hero.viewed");
              }}
              onPlaying={() => {
                // iOS can start playing without ever reporting loadeddata.
                if (!still) setLoaded(true);
                setPlaying(true);
                trackOnce("video.played");
              }}
              onError={() => {
                setVideoFailed(true);
                if (!still) setLoaded(true);
              }}
            />
          )}

          {/* On the picture, small, obviously optional. It used to be a
              full-width button competing with the one that matters. */}
          {model && loaded && (
            <button
              className="hero-pill"
              disabled={loading3d}
              onClick={() => void open3d()}
            >
              {/* Says what it is rather than what to do with it. "Drag to turn
                  it around" only means something once you already know there is
                  a model behind it, and most people never found out. */}
              <Cube size={15} />
              {t.t("see3d")}
            </button>
          )}
        </div>
      )}

      {!fixing && base}

      {look}

      {photoUrl && (
        <button
          className="sculpted"
          onClick={() => {
            setZoomList(undefined);
            setZoomed(photoUrl);
            trackOnce("photo.opened", { which: "theirs" });
          }}
        >
          <img className="thumb" src={photoUrl} alt="" loading="lazy" />
          <span className="tiny muted">{t.t("sculpted")}</span>
        </button>
      )}

      {/* Everything above is a picture on a screen; this is the thing in the
          hand it becomes, said where the drawing is rather than after the
          proof strip, which a third of the people who open the page never
          reach. The printed one opens first: it is the one they have not
          seen, and the preview is one swipe away for the comparison. */}
      {prints && (
        <button
          className="prints"
          onClick={() => {
            setZoomList([prints.preview.lg, prints.real.lg]);
            setZoomed(prints.real.lg);
            trackOnce("photo.opened", { which: "prints" });
          }}
        >
          <span className="prints-shots" aria-hidden="true">
            <img src={prints.preview.sm} alt="" loading="lazy" width={40} height={52} />
            <Chevron size={12} />
            <img src={prints.real.sm} alt="" loading="lazy" width={40} height={52} />
          </span>
          <span className="prints-text">
            <strong>{t.t("printsTitle")}</strong>
            <span className="tiny muted">{t.t("printsHint")}</span>
          </span>
        </button>
      )}

      {feedback}

      {footer}

      {zoomed && (
        <Lightbox
          src={zoomed}
          list={zoomList}
          onClose={() => {
            setZoomed(null);
            setZoomList(undefined);
          }}
        />
      )}
    </div>
  );
}
